import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Search, 
  MapPin, 
  Building2, 
  Languages, 
  Clock,
  Mail as MailIcon,
  DollarSign,
  X,
  Briefcase,
  CheckCircle,
  Globe,
  Phone
} from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import SEO from "@/components/SEO";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const DiveCentersDirectory = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [diveCenters, setDiveCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [contactModal, setContactModal] = useState(null);
  const [contactMessage, setContactMessage] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [sendingContact, setSendingContact] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed);
        setContactEmail(parsed.email || "");
      } catch (e) {
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchDiveCenters();
    }
  }, [user]);

  const fetchDiveCenters = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const res = await axios.get(`${API}/dive-center-accounts/recruiting`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiveCenters(res.data);
    } catch (error) {
      console.error("Failed to fetch dive centers:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied. Only dive professionals can view this page.");
      } else {
        toast.error("Failed to load dive centers");
      }
    } finally {
      setLoading(false);
    }
  };

  const canContact = user?.role === "pro" || user?.role === "admin";

  const handleContact = async () => {
    if (!contactMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSendingContact(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.post(`${API}/contact/dive-center`, {
        dive_center_user_id: contactModal.id,
        message: contactMessage,
        sender_email: contactEmail || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message sent successfully!");
      setContactModal(null);
      setContactMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSendingContact(false);
    }
  };

  const filteredCenters = diveCenters.filter(dc => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = dc.name?.toLowerCase() || "";
    const centerInfo = dc.dive_center_info || {};
    const location = centerInfo.location?.toLowerCase() || "";
    const country = centerInfo.country?.toLowerCase() || "";
    return name.includes(term) || location.includes(term) || country.includes(term);
  });

  // Access check
  if (!user) {
    return (
      <div className="min-h-screen">
        <SEO title="Recruiting Dive Centers | George Dives" />
        <Navigation />
        <div className="pt-32 pb-16">
          <div className="container mx-auto px-4 text-center">
            <Building2 className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-4">Recruiting Dive Centers</h1>
            <p className="text-gray-600 mb-8">Please log in to view dive centers that are currently hiring.</p>
            <Button onClick={() => navigate("/account")} className="bg-cyan-600 hover:bg-cyan-700">
              Log In
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (user.role !== "pro" && user.role !== "admin") {
    return (
      <div className="min-h-screen">
        <SEO title="Recruiting Dive Centers | George Dives" />
        <Navigation />
        <FloatingUserMenu />
        <div className="pt-32 pb-16">
          <div className="container mx-auto px-4 text-center">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-4">Access Restricted</h1>
            <p className="text-gray-600 mb-8">This page is only available to dive professionals.</p>
            <Button onClick={() => navigate("/community")} variant="outline">
              Back to Community
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO title="Recruiting Dive Centers | George Dives" />
      <Navigation />
      <FloatingUserMenu />
      
      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Recruiting Dive Centers</h1>
            <p className="text-gray-600">
              Discover dive centers looking for talented professionals like you
            </p>
          </div>

          {/* Search */}
          <div className="mb-8">
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search by name or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading dive centers...</p>
            </div>
          ) : filteredCenters.length === 0 ? (
            <Card className="p-12 text-center">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Recruiting Dive Centers</h3>
              <p className="text-gray-500">Check back later for new opportunities</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredCenters.map((dc) => {
                const profile = dc.dive_center_profile || {};
                const jobDetails = profile.job_details || {};
                const centerInfo = dc.dive_center_info || {};
                
                return (
                  <Card key={dc.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
                      <div className="flex items-center gap-4">
                        {centerInfo.logo_url ? (
                          <img 
                            src={centerInfo.logo_url.startsWith('http') ? centerInfo.logo_url : `${BACKEND_URL}${centerInfo.logo_url}`}
                            alt={centerInfo.name}
                            className="w-14 h-14 rounded-lg bg-white object-contain p-1"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-white/20 flex items-center justify-center">
                            <Building2 className="w-8 h-8" />
                          </div>
                        )}
                        <div>
                          <CardTitle className="text-xl text-white">
                            {centerInfo.name || dc.name || "Dive Center"}
                          </CardTitle>
                          {centerInfo.location && (
                            <div className="flex items-center gap-1 text-white/80 text-sm mt-1">
                              <MapPin className="w-4 h-4" />
                              {centerInfo.location}{centerInfo.country && `, ${centerInfo.country}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 text-green-600 mb-4">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Currently Recruiting</span>
                      </div>

                      {/* Dive Center Info */}
                      {centerInfo.description && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-gray-700">{centerInfo.description}</p>
                        </div>
                      )}

                      {/* Specialties */}
                      {centerInfo.specialties?.length > 0 && (
                        <div className="mb-4">
                          <div className="flex flex-wrap gap-1">
                            {centerInfo.specialties.map((specialty, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded-full text-xs">
                                {specialty}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Contact Info */}
                      {(centerInfo.website || centerInfo.email || centerInfo.phone) && (
                        <div className="mb-4 flex flex-wrap gap-3 text-sm text-gray-600">
                          {centerInfo.website && (
                            <a href={centerInfo.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-cyan-600">
                              <Globe className="w-4 h-4" />
                              Website
                            </a>
                          )}
                          {centerInfo.email && (
                            <span className="flex items-center gap-1">
                              <MailIcon className="w-4 h-4" />
                              {centerInfo.email}
                            </span>
                          )}
                          {centerInfo.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {centerInfo.phone}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Job Details */}
                      <div className="space-y-3 pt-3 border-t">
                        <h4 className="font-medium text-gray-800 flex items-center gap-2">
                          <Briefcase className="w-4 h-4" />
                          Job Opportunity
                        </h4>
                        
                        {jobDetails.qualifications && (
                          <div>
                            <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                              <span>Required Qualifications</span>
                            </div>
                            <p className="text-sm text-gray-700">{jobDetails.qualifications}</p>
                          </div>
                        )}

                        {jobDetails.duration && (
                          <div>
                            <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                              <Clock className="w-4 h-4" />
                              <span>Duration</span>
                            </div>
                            <p className="text-sm text-gray-700">{jobDetails.duration}</p>
                          </div>
                        )}

                        {jobDetails.salary && (
                          <div>
                            <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                              <DollarSign className="w-4 h-4" />
                              <span>Compensation</span>
                            </div>
                            <p className="text-sm text-gray-700">{jobDetails.salary}</p>
                          </div>
                        )}

                        {jobDetails.languages?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1 text-sm text-gray-500 mb-1">
                              <Languages className="w-4 h-4" />
                              <span>Languages Required</span>
                            </div>
                            <p className="text-sm text-gray-700">{jobDetails.languages.join(", ")}</p>
                          </div>
                        )}

                        {jobDetails.description && (
                          <div>
                            <p className="text-sm text-gray-600 mt-3 pt-3 border-t">
                              {jobDetails.description}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Contact Button */}
                      {canContact && (
                        <Button
                          onClick={() => setContactModal(dc)}
                          className="w-full mt-4 bg-cyan-600 hover:bg-cyan-700"
                        >
                          <MailIcon className="w-4 h-4 mr-2" />
                          Apply / Contact
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Contact Modal */}
      {contactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  Contact {contactModal.dive_center_info?.name || contactModal.name}
                </CardTitle>
                <button onClick={() => setContactModal(null)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Your Email (for reply)</Label>
                <Input
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Introduce yourself, your qualifications, and why you're interested..."
                  rows={5}
                />
              </div>
              <Button
                onClick={handleContact}
                disabled={sendingContact}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
              >
                {sendingContact ? "Sending..." : "Send Application"}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Your message and profile info will be sent via George Dives. The dive center can reply directly to your email.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DiveCentersDirectory;
